import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, Filter, ExternalLink, Monitor } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import './MonitorPedidos.css';

interface Pedido {
  id: number;
  mesa: string;
  status: string;
  total: number;
  observacoes?: string;
  created_at: string;
  total_itens?: number;
  itens?: PedidoItem[];
  [key: string]: any;
}

interface PedidoItem {
  id: number;
  produto_nome: string;
  quantidade: number;
  preco_unitario: number;
  observacoes: string;
  personalizacoes?: Personalizacao[] | string | null;
}

interface Personalizacao {
  id: number;
  nome: string;
  tipo: 'adicionar' | 'remover';
  quantidade: number;
  preco_adicional: number;
}

interface ChamadaGarcom {
  id: number;
  mesa: string;
  status: string;
  created_at: string;
  empresa_id: number;
}

const MonitorPedidos: React.FC = () => {
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [chamadasGarcom, setChamadasGarcom] = useState<ChamadaGarcom[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCancelados, setShowCancelados] = useState(false);
  const [showConcluidos, setShowConcluidos] = useState(false);

  useEffect(() => {
    fetchPedidos();
    fetchChamadasGarcom();
    const interval = setInterval(() => {
      fetchPedidos();
      fetchChamadasGarcom();
    }, 15000); // Atualiza a cada 15 segundos para o monitor
    return () => clearInterval(interval);
  }, []);

  const fetchPedidos = async () => {
    try {
      const response = await api.get('/api/pedidos');
      setPedidos(response.data);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  };

  const fetchChamadasGarcom = async () => {
    try {
      const response = await api.get('/api/chamar-garcom/1');
      setChamadasGarcom(response.data);
    } catch (error) {
      console.error('Erro ao carregar chamadas de garçom:', error);
    }
  };

  const updateStatus = async (id: number, status: string) => {
    try {
      await api.put(`/api/pedidos/${id}/status`, { status });
      toast.success('Status atualizado com sucesso!');
      fetchPedidos();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const updateStatusChamada = async (id: number, status: string) => {
    try {
      await api.put(`/api/chamar-garcom/${id}/status`, { status });
      toast.success('Status da chamada atualizado com sucesso!');
      fetchChamadasGarcom();
    } catch (error) {
      toast.error('Erro ao atualizar status da chamada');
    }
  };



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pendente':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'preparando':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'pronto':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'entregue':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      case 'cancelado':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pendente':
        return <Clock className="h-6 w-6" />;
      case 'preparando':
        return <Clock className="h-6 w-6" />;
      case 'pronto':
        return <CheckCircle className="h-6 w-6" />;
      case 'entregue':
        return <CheckCircle className="h-6 w-6" />;
      case 'cancelado':
        return <XCircle className="h-6 w-6" />;
      default:
        return <Clock className="h-6 w-6" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const enviarWhatsApp = (pedido: Pedido) => {
    // Verificar se o pedido tem telefone (apenas pedidos de entrega)
    if (!pedido.telefone_cliente) {
      toast.error('Este pedido não possui telefone para contato');
      return;
    }
    
    // Formatar número do telefone (remover caracteres especiais)
    const telefone = pedido.telefone_cliente.replace(/\D/g, '');
    
    // Criar mensagem com todas as informações do pedido
    let mensagem = `*PEDIDO #${pedido.id}*\n\n`;
    
    // Informações básicas
    mensagem += `*Status:* ${pedido.status.toUpperCase()}\n`;
    mensagem += `*Horário:* ${formatDate(pedido.created_at)}\n`;
    mensagem += `*Total:* R$ ${pedido.total.toFixed(2)}\n\n`;
    
    // Tipo de pedido
    if (pedido.tipo_pedido === 'entrega') {
      mensagem += `*TIPO:* ENTREGA\n`;
      mensagem += `*Endereço:* ${(() => {
        try {
          // Se já é um objeto, usar diretamente
          let endereco = pedido.endereco_entrega;
          if (typeof endereco === 'string') {
            endereco = JSON.parse(endereco);
          }
          
          // Verificar se é um objeto válido
          if (typeof endereco === 'object' && endereco !== null) {
            const partes = [];
            if (endereco.rua) partes.push(endereco.rua);
            if (endereco.numero_casa) partes.push(endereco.numero_casa);
            if (endereco.bairro) partes.push(endereco.bairro);
            if (endereco.cidade) partes.push(endereco.cidade);
            if (endereco.estado) partes.push(endereco.estado);
            if (endereco.cep) partes.push(`CEP: ${endereco.cep}`);
            
            return partes.length > 0 ? partes.join(' - ') : 'Endereço não disponível';
          }
          
          // Se não for objeto nem string válida, retornar string
          return String(pedido.endereco_entrega);
        } catch {
          // Se falhar o parse, retornar como string
          return String(pedido.endereco_entrega);
        }
      })()}\n\n`;
    } else {
      mensagem += `*TIPO:* MESA ${pedido.mesa}\n\n`;
    }
    
    // Informações de pagamento
    mensagem += `*PAGAMENTO:*\n`;
    mensagem += `• Tipo: ${pedido.metodo_pagamento === 'entrega' ? 'Na Entrega' : 'Pelo Site'}\n`;
    
    if (pedido.metodo_pagamento_detalhado) {
      const metodo = (() => {
        switch (pedido.metodo_pagamento_detalhado) {
          case 'dinheiro': return 'Dinheiro';
          case 'cartao': return 'Cartão';
          case 'pix': return 'PIX';
          case 'pix_site': return 'PIX pelo Site';
          case 'cartao_site': return 'Cartão pelo Site';
          default: return pedido.metodo_pagamento_detalhado;
        }
      })();
      mensagem += `• Método: ${metodo}\n`;
    }
    
    if (pedido.precisa_troco && pedido.valor_troco) {
      const valor = typeof pedido.valor_troco === 'string' 
        ? parseFloat(pedido.valor_troco) 
        : pedido.valor_troco;
      mensagem += `• Troco para: R$ ${isNaN(valor) ? '0.00' : valor.toFixed(2)}\n`;
    }
    
    mensagem += `• Status: ${pedido.status_pagamento || 'Pendente'}\n\n`;
    
    // Detalhes dos itens do pedido
    if (pedido.itens && pedido.itens.length > 0) {
      mensagem += `*ITENS DO PEDIDO:*\n`;
      pedido.itens.forEach((item, index) => {
        mensagem += `${index + 1}. ${item.quantidade}x ${item.produto_nome}\n`;
        
        // Personalizações
        if (item.personalizacoes) {
          // Garantir que personalizacoes seja um array
          let personalizacoes = item.personalizacoes;
          
          // Se for string, tentar fazer parse
          if (typeof personalizacoes === 'string') {
            try {
              personalizacoes = JSON.parse(personalizacoes);
            } catch {
              personalizacoes = [];
            }
          }
          
          // Se não for array, converter para array vazio
          if (!Array.isArray(personalizacoes)) {
            personalizacoes = [];
          }
          
          if (personalizacoes.length > 0) {
            // Adicionais
            const adicionais = personalizacoes.filter(p => p && p.tipo === 'adicionar');
            if (adicionais.length > 0) {
              mensagem += `   + Adicionais: ${adicionais.map(p => `${p.quantidade || 1}x ${p.nome || ''}`).join(', ')}\n`;
            }
            
            // Remoções
            const remocoes = personalizacoes.filter(p => p && p.tipo === 'remover');
            if (remocoes.length > 0) {
              mensagem += `   - Remover: ${remocoes.map(p => `${p.quantidade || 1}x ${p.nome || ''}`).join(', ')}\n`;
            }
          }
        }
        
        // Observações do item
        if (item.observacoes) {
          mensagem += `   Obs: ${item.observacoes}\n`;
        }
        
        mensagem += `\n`;
      });
    }
    
    // Observações gerais
    if (pedido.observacoes) {
      mensagem += `*Observações Gerais:* ${pedido.observacoes}\n\n`;
    }
    
    // URL para abrir no WhatsApp Web
    const url = `https://wa.me/55${telefone}?text=${encodeURIComponent(mensagem)}`;
    window.open(url, '_blank');
  };

  const pedidosFiltrados = pedidos.filter(pedido => {
    if (pedido.status === 'cancelado' && !showCancelados) return false;
    if (pedido.status === 'entregue' && !showConcluidos) return false;
    return true;
  });

  const pedidosPorStatus = {
    ativos: pedidosFiltrados.filter(p => ['pendente', 'preparando', 'pronto'].includes(p.status)),
    cancelados: pedidos.filter(p => p.status === 'cancelado'),
    concluidos: pedidos.filter(p => p.status === 'entregue')
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 monitor-container">
      {/* Header do Monitor */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8 monitor-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Monitor className="h-12 w-12 text-blue-600" />
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
                🍽️ Monitor de Pedidos
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-400">
                Controle em tempo real - {new Date().toLocaleString('pt-BR')}
              </p>
            </div>
          </div>
          
          <div className="text-right pedidos-counter">
            <div className="text-3xl font-bold text-white">
              {pedidosPorStatus.ativos.length}
            </div>
            <div className="text-lg text-white/90">
              Pedidos Ativos
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-8 monitor-filters">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <Filter className="h-6 w-6 text-gray-500" />
            <span className="text-lg font-medium text-gray-700 dark:text-gray-300">
              Filtros:
            </span>
          </div>
          
          <button
            onClick={() => setShowCancelados(!showCancelados)}
            className={`px-6 py-3 rounded-xl text-lg font-medium transition-all transform hover:scale-105 ${
              showCancelados 
                ? 'bg-red-100 text-red-800 border-2 border-red-300' 
                : 'bg-gray-100 text-gray-600 border-2 border-gray-300'
            }`}
          >
            ❌ Cancelados ({pedidosPorStatus.cancelados.length})
          </button>
          
          <button
            onClick={() => setShowConcluidos(!showConcluidos)}
            className={`px-6 py-3 rounded-xl text-lg font-medium transition-all transform hover:scale-105 ${
              showConcluidos 
                ? 'bg-green-100 text-green-800 border-2 border-green-300' 
                : 'bg-gray-100 text-gray-600 border-2 border-gray-300'
            }`}
          >
            ✅ Concluídos ({pedidosPorStatus.concluidos.length})
          </button>
        </div>
      </div>

      {/* Chamadas de Garçom */}
      {chamadasGarcom.length > 0 && (
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            🚨 Chamadas de Garçom ({chamadasGarcom.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {chamadasGarcom.map((chamada) => (
              <div
                key={chamada.id}
                className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-300 dark:border-yellow-600 rounded-xl p-6 animate-pulse shadow-lg chamada-garcom"
              >
                <div className="text-center">
                  <div className="text-4xl mb-3">🚨</div>
                  <h3 className="text-2xl font-bold text-yellow-800 dark:text-yellow-200 mb-2">
                    Mesa {chamada.mesa}
                  </h3>
                  <p className="text-lg text-yellow-600 dark:text-yellow-400 mb-4">
                    #{chamada.id}
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-4">
                    {formatDate(chamada.created_at)}
                  </p>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => updateStatusChamada(chamada.id, 'atendida')}
                      className="flex-1 bg-green-500 text-white text-lg py-3 rounded-lg hover:bg-green-600 transition-colors font-medium"
                    >
                      ✅ Atendida
                    </button>
                    <button
                      onClick={() => updateStatusChamada(chamada.id, 'cancelada')}
                      className="flex-1 bg-red-500 text-white text-lg py-3 rounded-lg hover:bg-red-600 transition-colors font-medium"
                    >
                      ❌ Cancelar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pedidos Ativos */}
      {pedidosPorStatus.ativos.length > 0 && (
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            🚀 Pedidos Ativos ({pedidosPorStatus.ativos.length})
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 monitor-grid">
            {pedidosPorStatus.ativos.map((pedido) => (
              <div
                key={pedido.id}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border-2 border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all transform hover:scale-105 monitor-card monitor-transition"
              >
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {pedido.tipo_pedido === 'entrega' ? '🛵 Entrega' : `🍽️ Mesa ${pedido.mesa}`}
                    </h3>
                    <p className="text-lg text-gray-500 dark:text-gray-400">
                      Pedido #{pedido.id}
                    </p>
                  </div>
                  <span className={`px-4 py-2 text-lg font-bold rounded-full flex items-center gap-2 border-2 ${getStatusColor(pedido.status)}`}>
                    {getStatusIcon(pedido.status)}
                    {pedido.status.toUpperCase()}
                  </span>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-xl">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">Total:</span>
                    <span className="font-bold text-2xl text-gray-900 dark:text-white">
                      R$ {pedido.total.toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between text-lg">
                    <span className="text-gray-600 dark:text-gray-400">Itens:</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {pedido.total_itens || 'N/A'}
                    </span>
                  </div>

                  <div className="text-lg">
                    <span className="text-gray-600 dark:text-gray-400 font-medium">Horário:</span>
                    <p className="text-gray-900 dark:text-white font-medium">
                      {formatDate(pedido.created_at)}
                    </p>
                  </div>

                  {/* Informações de Entrega para pedidos de entrega */}
                  {pedido.tipo_pedido === 'entrega' && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                      <h4 className="font-medium text-blue-800 dark:text-blue-200 text-base mb-3">
                        📍 Informações de Entrega
                      </h4>
                      <div className="space-y-3 text-sm">
                        {pedido.nome_cliente && pedido.sobrenome_cliente && (
                          <div className="flex justify-between">
                            <span className="text-blue-600 dark:text-blue-400 font-medium">Cliente:</span>
                            <span className="text-blue-800 dark:text-blue-200 font-bold">
                              {pedido.nome_cliente} {pedido.sobrenome_cliente}
                            </span>
                          </div>
                        )}
                        {pedido.telefone_cliente && (
                          <div className="flex justify-between items-center">
                            <span className="text-blue-600 dark:text-blue-400 font-medium">Telefone:</span>
                            <div className="flex items-center gap-2">
                              <span className="text-blue-800 dark:text-blue-200 font-bold">
                                {pedido.telefone_cliente}
                              </span>
                  <button
                                onClick={() => enviarWhatsApp(pedido)}
                                className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-full transition-colors"
                                title="Enviar WhatsApp com detalhes do pedido"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.87 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        )}
                        {pedido.email_cliente && (
                          <div className="flex justify-between">
                            <span className="text-blue-600 dark:text-blue-400 font-medium">Email:</span>
                            <span className="text-blue-800 dark:text-blue-200 font-bold">
                              {pedido.email_cliente}
                            </span>
                          </div>
                        )}
                        {pedido.endereco_entrega && (
                          <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-600">
                            <div className="flex justify-between">
                              <span className="text-blue-600 dark:text-blue-400 font-medium">Endereço:</span>
                            </div>
                            <div className="text-blue-800 dark:text-blue-200 text-sm mt-1">
                              {(() => {
                                try {
                                  // Se já é um objeto, usar diretamente
                                  let endereco = pedido.endereco_entrega;
                                  if (typeof endereco === 'string') {
                                    endereco = JSON.parse(endereco);
                                  }
                                  
                                  // Verificar se é um objeto válido
                                  if (typeof endereco === 'object' && endereco !== null) {
                                    const partes = [];
                                    if (endereco.rua) partes.push(endereco.rua);
                                    if (endereco.numero_casa) partes.push(endereco.numero_casa);
                                    if (endereco.bairro) partes.push(endereco.bairro);
                                    if (endereco.cidade) partes.push(endereco.cidade);
                                    if (endereco.estado) partes.push(endereco.estado);
                                    if (endereco.cep) partes.push(`CEP: ${endereco.cep}`);
                                    
                                    return partes.length > 0 ? partes.join(' - ') : 'Endereço não disponível';
                                  }
                                  
                                  // Se não for objeto nem string válida, retornar string
                                  return String(pedido.endereco_entrega);
                                } catch {
                                  // Se falhar o parse, retornar como string
                                  return String(pedido.endereco_entrega);
                                }
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Informações de Pagamento */}
                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
                    <h4 className="font-medium text-purple-800 dark:text-purple-200 text-base mb-3">
                      💳 Informações de Pagamento
                    </h4>
                    <div className="space-y-3 text-sm">
                      {/* Tipo de Pagamento */}
                      <div className="flex justify-between">
                        <span className="text-purple-600 dark:text-purple-400 font-medium">Tipo:</span>
                        <span className="text-purple-800 dark:text-purple-200 font-bold">
                          {pedido.metodo_pagamento === 'entrega' ? '🛵 Pagamento na Entrega' : '🌐 Pagamento pelo Site'}
                        </span>
                      </div>

                      {/* Método Específico */}
                      <div className="flex justify-between">
                        <span className="text-purple-600 dark:text-purple-400 font-medium">Método:</span>
                        <span className="text-purple-800 dark:text-purple-200 font-bold">
                          {(() => {
                            switch (pedido.metodo_pagamento_detalhado) {
                              case 'dinheiro':
                                return '💰 Dinheiro';
                              case 'cartao':
                                return '💳 Cartão (Débito/Crédito)';
                              case 'pix':
                                return '📱 PIX';
                              case 'pix_site':
                                return '🌐 PIX pelo Site';
                              case 'cartao_site':
                                return '💳 Cartão pelo Site';
                              default:
                                return pedido.metodo_pagamento_detalhado || '❓ Não informado';
                            }
                          })()}
                        </span>
                      </div>

                      {/* Status do Pagamento */}
                      {pedido.status_pagamento && (
                        <div className="flex justify-between">
                          <span className="text-purple-600 dark:text-purple-400 font-medium">Status:</span>
                          <span className={`font-bold ${
                            pedido.status_pagamento === 'pago' 
                              ? 'text-green-600 dark:text-green-400' 
                              : pedido.status_pagamento === 'cancelado'
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-yellow-600 dark:text-yellow-400'
                          }`}>
                            {pedido.status_pagamento === 'pago' ? '✅ Pago' : 
                             pedido.status_pagamento === 'cancelado' ? '❌ Cancelado' : 
                             '⏳ Pendente'}
                          </span>
                        </div>
                      )}

                                             {/* Informações de Troco (apenas para dinheiro) */}
                       {pedido.metodo_pagamento_detalhado === 'dinheiro' && pedido.precisa_troco && (
                         <div className="mt-3 pt-3 border-t border-purple-200 dark:border-purple-600">
                           <div className="flex justify-between">
                             <span className="text-purple-600 dark:text-purple-400 font-medium">Troco para:</span>
                             <span className="text-purple-800 dark:text-purple-200 font-bold">
                               R$ {(() => {
                                 // Verificar se precisa_troco é true e se valor_troco existe
                                 if (pedido.precisa_troco && pedido.valor_troco) {
                                   // Converter para número se for string
                                   const valor = typeof pedido.valor_troco === 'string' 
                                     ? parseFloat(pedido.valor_troco) 
                                     : pedido.valor_troco;
                                   return isNaN(valor) ? '0.00' : valor.toFixed(2);
                                 }
                                 return '0.00';
                               })()}
                             </span>
                           </div>
                         </div>
                       )}
                      
                      {/* Detalhes JSON (para debug) */}
                      {pedido.detalhes_pagamento_json && (
                        <details className="mt-2">
                          <summary className="text-purple-600 dark:text-purple-400 cursor-pointer text-xs">
                            📋 Ver detalhes completos
                          </summary>
                          <div className="mt-1 p-2 bg-purple-100 dark:bg-purple-800/30 rounded text-xs font-mono text-purple-800 dark:text-purple-200">
                            {JSON.stringify(JSON.parse(pedido.detalhes_pagamento_json), null, 2)}
                          </div>
                        </details>
                      )}
                    </div>
                  </div>

                  {/* Detalhes dos itens e personalizações */}
                    <div className="border-t-2 border-gray-200 dark:border-gray-700 pt-4 space-y-4">
                      <h4 className="font-bold text-xl text-gray-900 dark:text-white text-center">
                        📋 Detalhes do Pedido
                      </h4>
                      
                      {!pedido.itens || pedido.itens.length === 0 ? (
                        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                          <p className="text-lg">⚠️ Detalhes dos itens não disponíveis</p>
                          <p className="text-sm">A API pode não estar retornando os detalhes completos</p>
                        </div>
                      ) : (
                        pedido.itens.map((item, index) => (
                          <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                            <div className="flex justify-between items-start mb-3">
                              <span className="font-bold text-lg text-gray-900 dark:text-white">
                                {item.quantidade}x {item.produto_nome}
                              </span>
                              <span className="text-lg text-gray-600 dark:text-gray-400 font-medium">
                                R$ {(item.preco_unitario * item.quantidade).toFixed(2)}
                              </span>
                            </div>
                            
                            {/* Personalizações */}
                            {(() => {
                              // Garantir que personalizacoes seja um array
                              let personalizacoes = item.personalizacoes;
                              
                              // Se for string, tentar fazer parse
                              if (typeof personalizacoes === 'string') {
                                try {
                                  personalizacoes = JSON.parse(personalizacoes);
                                } catch {
                                  personalizacoes = [];
                                }
                              }
                              
                              // Se não for array, converter para array vazio
                              if (!Array.isArray(personalizacoes)) {
                                personalizacoes = [];
                              }
                              
                              // Se não houver personalizações, não renderizar nada
                              if (!personalizacoes || personalizacoes.length === 0) {
                                return null;
                              }
                              
                              const adicionais = personalizacoes.filter(p => p && p.tipo === 'adicionar');
                              const remocoes = personalizacoes.filter(p => p && p.tipo === 'remover');
                              
                              // Se não houver adicionais nem remoções, não renderizar nada
                              if (adicionais.length === 0 && remocoes.length === 0) {
                                return null;
                              }
                              
                              return (
                                <div className="space-y-3">
                                  {/* Adicionais */}
                                  {adicionais.length > 0 && (
                                    <div>
                                      <span className="text-sm text-green-600 dark:text-green-400 font-bold">
                                        ➕ Adicionais:
                                      </span>
                                      <div className="ml-3 space-y-1">
                                        {adicionais.map((p, idx) => (
                                          <div key={idx} className="flex justify-between text-sm">
                                            <span className="text-gray-600 dark:text-gray-400">
                                              {p.quantidade}x {p.nome}
                                            </span>
                                            <span className="text-green-600 dark:text-green-400 font-medium">
                                              +R$ {((p.preco_adicional || 0) * (p.quantidade || 1)).toFixed(2)}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Remoções */}
                                  {remocoes.length > 0 && (
                                    <div>
                                      <span className="text-sm text-orange-600 dark:text-orange-400 font-bold">
                                        ➖ Remover:
                                      </span>
                                      <div className="ml-3 space-y-1">
                                        {remocoes.map((p, idx) => (
                                          <div key={idx} className="text-sm text-gray-600 dark:text-gray-400">
                                            {p.quantidade}x {p.nome}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            
                            {/* Observações do item */}
                            {item.observacoes && (
                              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                                  💬 {item.observacoes}
                                </span>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>

                  {pedido.observacoes && (
                    <div className="text-lg">
                      <span className="text-gray-600 dark:text-gray-400 font-medium">Observações:</span>
                      <p className="text-gray-900 dark:text-white mt-2 font-medium">
                        {pedido.observacoes}
                      </p>
                    </div>
                  )}
                </div>

                <div className="pt-6 border-t-2 border-gray-200 dark:border-gray-700">
                  <div className="flex gap-3">
                    {pedido.status === 'pendente' && (
                      <>
                        <button
                          onClick={() => updateStatus(pedido.id, 'preparando')}
                          className="flex-1 bg-blue-500 text-white text-lg py-4 rounded-lg hover:bg-blue-600 transition-colors font-bold"
                        >
                          🔄 Preparar
                        </button>
                        <button
                          onClick={() => updateStatus(pedido.id, 'cancelado')}
                          className="flex-1 bg-red-500 text-white text-lg py-4 rounded-lg hover:bg-red-600 transition-colors font-bold"
                        >
                          ❌ Cancelar
                        </button>
                      </>
                    )}
                    
                    {pedido.status === 'preparando' && (
                      <button
                        onClick={() => updateStatus(pedido.id, 'pronto')}
                        className="w-full bg-green-500 text-white text-lg py-4 rounded-lg hover:bg-green-600 transition-colors font-bold"
                      >
                        ✅ Pronto
                      </button>
                    )}
                    
                    {pedido.status === 'pronto' && (
                      <button
                        onClick={() => updateStatus(pedido.id, 'entregue')}
                        className="w-full bg-green-500 text-white text-lg py-4 rounded-lg hover:bg-green-600 transition-colors font-bold"
                      >
                        🚚 Entregue
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pedidos Cancelados */}
      {showCancelados && pedidosPorStatus.cancelados.length > 0 && (
        <div className="mt-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            ❌ Pedidos Cancelados ({pedidosPorStatus.cancelados.length})
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {pedidosPorStatus.cancelados.map((pedido) => (
              <div
                key={pedido.id}
                className="bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-600 rounded-xl p-6 shadow-lg"
              >
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-red-800 dark:text-red-200">
                      {pedido.tipo_pedido === 'entrega' ? '🛵 Entrega' : `🍽️ Mesa ${pedido.mesa}`}
                    </h3>
                    <p className="text-lg text-red-600 dark:text-red-400">
                      Pedido #{pedido.id}
                    </p>
                  </div>
                  <span className="px-4 py-2 text-lg font-bold bg-red-100 text-red-800 border-2 border-red-300 rounded-full">
                    ❌ CANCELADO
                  </span>
                </div>

                <div className="space-y-4 text-lg">
                  <div className="flex justify-between">
                    <span className="text-red-600 dark:text-red-400 font-medium">Total:</span>
                    <span className="font-bold text-2xl text-red-800 dark:text-red-200">
                      R$ {pedido.total.toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-red-600 dark:text-red-400 font-medium">Itens:</span>
                    <span className="text-red-800 dark:text-red-200 font-medium">
                      {pedido.total_itens}
                    </span>
                  </div>

                  <div>
                    <span className="text-red-600 dark:text-red-400 font-medium">Horário:</span>
                    <p className="text-red-800 dark:text-red-200 font-medium">
                      {formatDate(pedido.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pedidos Concluídos */}
      {showConcluidos && pedidosPorStatus.concluidos.length > 0 && (
        <div className="mt-12">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-3">
            ✅ Pedidos Concluídos ({pedidosPorStatus.concluidos.length})
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {pedidosPorStatus.concluidos.map((pedido) => (
              <div
                key={pedido.id}
                className="bg-green-50 dark:bg-green-900/20 border-2 border-green-300 dark:border-green-600 rounded-xl p-6 shadow-lg"
              >
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-green-800 dark:text-green-200">
                      {pedido.tipo_pedido === 'entrega' ? '🛵 Entrega' : `🍽️ Mesa ${pedido.mesa}`}
                    </h3>
                    <p className="text-lg text-green-600 dark:text-green-400">
                      Pedido #{pedido.id}
                    </p>
                  </div>
                  <span className="px-4 py-2 text-lg font-bold bg-green-100 text-green-800 border-2 border-green-300 rounded-full">
                    ✅ ENTREGUE
                  </span>
                </div>

                <div className="space-y-4 text-lg">
                  <div className="flex justify-between">
                    <span className="text-green-600 dark:text-green-400 font-medium">Total:</span>
                    <span className="font-bold text-2xl text-green-800 dark:text-green-200">
                      R$ {pedido.total.toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-green-600 dark:text-green-400 font-medium">Itens:</span>
                    <span className="text-green-800 dark:text-green-200 font-medium">
                      {pedido.total_itens}
                    </span>
                  </div>

                  <div>
                    <span className="text-green-600 dark:text-green-400 font-medium">Horário:</span>
                    <p className="text-green-800 dark:text-green-200 font-medium">
                      {formatDate(pedido.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {pedidosFiltrados.length === 0 && (
        <div className="text-center py-20">
          <p className="text-2xl text-gray-500 dark:text-gray-400">
            Nenhum pedido encontrado
          </p>
        </div>
      )}

      {/* Footer do Monitor */}
      <div className="mt-16 text-center text-gray-500 dark:text-gray-400 monitor-footer">
        <p className="text-lg">
          Monitor de Pedidos - Atualizado automaticamente a cada 15 segundos
        </p>
        <p className="text-sm mt-2">
          Última atualização: {new Date().toLocaleString('pt-BR')}
        </p>
      </div>
    </div>
  );
};

export default MonitorPedidos;
